import React from "react";
import { Phone, MapPin, Mail } from "lucide-react";
import { PHONE, PHONE_TEL, EMAIL, ADDRESS, CALENDLY_URL, NAV_LINKS, LOGO_URL } from "@/lib/siteData";

import landing from "@/lib/landing.json";
import { trackersConfigured } from "@/lib/tracking";
import { openCookieSettings } from "@/components/CookieBanner";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-md-primary text-white/85 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 pb-14 border-b border-white/15">
          <div className="md:col-span-4">
            <div className="bg-white rounded-2xl p-4 inline-block mb-5">
              <img src={LOGO_URL} alt="MD Courtage" className="h-16 w-auto object-contain" />
            </div>
            <p className="text-sm leading-relaxed text-white/70 max-w-sm">
              Courtier en assurance, crédit et placement dans les départements 53 (Mayenne)
              et 35 (Ille-et-Vilaine). Conseil personnalisé, transparence sur la
              rémunération, accompagnement long terme.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.25em] text-md-secondary font-bold mb-5">Navigation</div>
            <ul className="space-y-2 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a href={l.page ? l.href : `/${l.href}`} data-testid={`footer-nav-${l.href.slice(1)}`} className="text-white/75 hover:text-md-secondary transition-colors">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-md-secondary font-bold mb-5">Contact</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-1 text-md-secondary" />
                <a href={`tel:${PHONE_TEL}`} className="hover:text-md-secondary">{PHONE}</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-1 text-md-secondary" />
                <a href={`mailto:${EMAIL}`} className="hover:text-md-secondary">{EMAIL}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-md-secondary" />
                <span>{ADDRESS}</span>
              </li>
            </ul>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-cta-rdv"
              className="inline-flex items-center gap-2 mt-6 bg-md-secondary hover:bg-md-secondaryHover text-white px-6 py-3 rounded-full text-sm font-semibold transition-all"
            >
              Prendre rendez-vous →
            </a>
          </div>
        </div>

        <div className="pt-10 text-xs text-white/55 leading-relaxed space-y-3">
          <p className="font-semibold text-white/80">Mentions légales</p>
          <p>
            MD Courtage — Maël DUBOIS, entrepreneur individuel — SIRET 812 084 259 00025 — RCS Paris n° 812 084 259, 60 rue François 1er, 75008 Paris.
            Orias n° 26006910 — <a href="https://www.orias.fr" className="underline hover:text-md-secondary" target="_blank" rel="noreferrer">www.orias.fr</a> :
            courtier en assurance (COA, exercice indépendant) ; mandataire d'intermédiaire d'assurance (MIA) pour le compte d'INOVEA ;
            courtier en opérations de banque et services de paiement (COBSP) ; mandataire d'intermédiaire en opérations de banque et
            services de paiement (MIOBSP) pour le compte de RB COURTAGE ; conseiller en investissements financiers (CIF) pour le compte
            d'INOVEA, membre de la CNCEF Patrimoine (association agréée par l'AMF) — conseil non indépendant.
          </p>
          <p>
            Sous le contrôle de l'ACPR (4 place de Budapest, CS 92459, 75436 Paris Cedex 09) et de l'AMF. Carte de transaction immobilière
            n° C3402AA27843 délivrée par la CCI de l'Hérault. Garantie financière et assurance RC professionnelle conformes au Code des
            assurances, police n° 127 128 662 souscrite auprès de MMA IARD (France métropolitaine, DROM et COM). Aucun lien capitalistique avec un assureur.
            Médiateur : Médiation Consommation Développement — <a href="https://www.medconsodev.eu" className="underline hover:text-md-secondary" target="_blank" rel="noreferrer">www.medconsodev.eu</a>.
          </p>
          <p>Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager. Les placements présentent un risque de perte en capital.</p>
          <div className="flex flex-wrap justify-between items-center pt-6 border-t border-white/10 mt-6">
            <p>© {new Date().getFullYear()} MD Courtage — Tous droits réservés.</p>
            <nav className="flex flex-wrap gap-x-4 gap-y-2" data-testid="footer-zones">
              <span className="text-white/50">Zones :</span>
              {landing.cities.map((c) => (
                <a key={c.slug} href={`/courtier/${c.slug}`} data-testid={`footer-city-${c.slug}`} className="text-white/70 hover:text-md-secondary transition-colors">Courtier {c.name}</a>
              ))}
            </nav>
            <nav className="flex flex-wrap gap-x-5 gap-y-2" data-testid="footer-legal-links">
              {trackersConfigured() && <button onClick={openCookieSettings} data-testid="footer-cookie-settings" className="text-white/70 hover:text-md-secondary transition-colors">Gérer les cookies</button>}
              {[["/cgu", "CGU"], ["/cgv", "CGV"], ["/confidentialite", "Confidentialité"], ["/consentement", "Gérer mon consentement"]].map(([h, l]) => (
                <a key={h} href={h} data-testid={`footer-link-${h.slice(1)}`} className="text-white/70 hover:text-md-secondary transition-colors">{l}</a>
              ))}
            </nav>
            <p className="text-white/40">Site refondu avec soin</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
